import React, { useCallback, useContext } from 'react';
import { Grid, IconButton, Tooltip } from '@material-ui/core';

import Progress from './Progress';
import {
  TimesCircleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  SycnIcon,
  ArrowAltCircleDownIcon,
  ArrowAltCircleUpIcon,
} from '../../../../components/icon/solid';

import { UploadPanelStyles } from './styles';
import { DownloadFile, FileMeta, UploadContext } from '../useUploadFiles';

type Props = {
  file?: FileMeta;
  downloadFile?: DownloadFile;
};

/* eslint-disable no-nested-ternary */

const Preview = ({ file, downloadFile }: Props): React.ReactElement => {
  const styles = UploadPanelStyles();
  const name = file ? file.meta.name : downloadFile ? downloadFile.name : '';
  const percent = file ? file.meta.percent : downloadFile ? downloadFile.percent : 0;
  const status = file ? file.meta.status : downloadFile ? downloadFile.status : 'done';

  const {
    cancel, restart, remove, cancelDownloadFile, removeDownloadFile, restartDownloadFile,
  } = useContext(UploadContext);
  const title = name || 'unknown';

  const handleRemove = useCallback((e: React.MouseEvent) => {
    if (file) {
      remove(file);
    } else if (downloadFile) {
      removeDownloadFile(downloadFile);
    }
  }, [file, downloadFile, remove, removeDownloadFile]);

  const handleCancel = useCallback((e: React.MouseEvent) => {
    if (file) {
      cancel(file);
    } else if (downloadFile) {
      cancelDownloadFile(downloadFile);
    }
  }, [file, downloadFile, cancel, cancelDownloadFile]);

  const handleRestart = useCallback((e: React.MouseEvent) => {
    if (file) {
      restart(file);
    } else if (downloadFile) {
      restartDownloadFile(downloadFile);
    }
  }, [file, downloadFile, restart, restartDownloadFile]);

  if (status === 'error_file_size' || status === 'error_validation') {
    return (
      <Grid container>
        <Grid item className={styles.titleContainer}>
          <span>{title}</span>
        </Grid>
        <Grid item className={styles.actionContainer}>
          <IconButton title="Remove" onClick={handleRemove} className={styles.action}>
            <TimesCircleIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid item container alignItems="center" className={styles.preview}>
      <Grid item container className={styles.titleContainer}>
        <Grid item style={{ display: 'flex', alignItems: 'center' }}>
          {file
            ? (<ArrowAltCircleUpIcon className={styles.uploadIcon} />)
            : (<ArrowAltCircleDownIcon className={styles.downloadIcon} />)}
        </Grid>
        <Grid item style={{ maxWidth: 230 }}>
          {title}
        </Grid>
      </Grid>
      <Grid item className={styles.actionContainer}>
        {
          status !== 'aborted' && (
            ['error_upload_params', 'exception_upload', 'error_upload', 'error'].includes(status)
              ? (
                <Tooltip title="File upload unsuccessful">
                  <ExclamationCircleIcon className={styles.errorIcon} />
                </Tooltip>
              )
              : (percent >= 100 && status === 'done')
                ? <CheckCircleIcon className={styles.completeIcon} />
                : <Progress value={percent} isStatic={downloadFile ? true : file?.isChunk || false} />
          )
        }
      </Grid>
      <Grid item>
        {
          (['uploading', 'downloading'].includes(status))
          && (
            <IconButton title="Cancel" onClick={handleCancel} className={styles.action}>
              <TimesCircleIcon />
            </IconButton>
          )
        }
        {
          (!['preparing', 'getting_upload_params', 'uploading', 'downloading'].includes(status))
          && (
            <IconButton title="Remove" onClick={handleRemove} className={styles.action}>
              <TimesCircleIcon />
            </IconButton>
          )
        }
        {
          ['error_upload_params', 'exception_upload', 'error_upload', 'error'].includes(status)
          && (
            <IconButton onClick={handleRestart} title="Restart upload" className={styles.action}>
              <SycnIcon />
            </IconButton>
          )
        }
      </Grid>
    </Grid>
  );
};

export default Preview;
